'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, type QueryKey } from '@tanstack/react-query'
import { useSessionEvents } from '@/hooks/useSession'
import { reportError, startMonitoring } from '@/lib/monitoring'

/** The entity a key names (`profile`), never the ids after it — one of them can be the person's own user id. */
const entityOf = (key: QueryKey | undefined): string | undefined => (key?.[0] === undefined ? undefined : String(key[0]))

/**
 * The app's one TanStack client. Every server fact — the session, the entitlement, the character
 * links — is a query on it, which is what lets a sign-out empty all of them in one call.
 *
 * The two caches are the one place a failed fetch becomes a report. A query library catches by
 * design: a rejected `queryFn` becomes `isError` and a tidy ErrorState, never an uncaught error, so
 * the window's handlers never fire and no error boundary sees it. Without these, an AppSync outage
 * looked to monitoring exactly like a healthy app. Everything is reported rather than a chosen few:
 * `retry: 1` means a failure has already failed twice, and monitoring.ts drops offline noise.
 * A query that fails as an EXPECTED outcome says so with `meta: { expectedFailure: true }`.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        queryCache: new QueryCache({
            onError: (error, query) => {
                if (query.meta?.['expectedFailure'] === true) { return }
                reportError(error, { boundary: 'query', key: entityOf(query.queryKey) })
            },
        }),
        mutationCache: new MutationCache({
            onError: (error, _variables, _onMutateResult, mutation) => reportError(error, { boundary: 'mutation', key: entityOf(mutation.options.mutationKey) }),
        }),
        defaultOptions: {
            queries: {
                staleTime: 60_000,
                refetchOnWindowFocus: false,
                // Nothing is reported as failed until a retry has also failed.
                retry: 1,
            },
        },
    })
}

/** Keeps the session query honest after it was first read — see useSessionEvents. Renders nothing. */
function SessionEvents() {
    useSessionEvents()
    return null
}

export function Providers({ children }: { children: ReactNode }) {
    const [client] = useState(createQueryClient)
    // Arms the deferred loader only: the SDK itself arrives on engagement or once the page settles.
    useEffect(startMonitoring, [])
    return (
        <QueryClientProvider client={client}>
            <SessionEvents />
            {children}
        </QueryClientProvider>
    )
}
